// Procedural Milky Way Galaxy scene with interactive controls.
// Written with React.createElement to bypass visual-edits attribute injection.
import { useRef, useMemo, useEffect, Suspense, createElement as h } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";

// ---- Procedural galaxy generator ----
function buildGalaxyGeometry({
  count = 90000,
  arms = 5,
  radius = 30,
  spin = 1.4,
  randomness = 0.5,
  randomnessPow = 2.4,
}) {
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const sizes = new Float32Array(count);
  // Multi-tone palette across radius bands for a colorful galaxy
  const palette = [
    new THREE.Color("#FFE0A8"), // inner warm core
    new THREE.Color("#FFB066"), // sub-inner amber
    new THREE.Color("#FF6FA0"), // pink/magenta arm
    new THREE.Color("#A077FF"), // violet arm
    new THREE.Color("#4FC1FF"), // cyan-blue outer
    new THREE.Color("#A0FFD4"), // teal rim
  ];
  for (let i = 0; i < count; i++) {
    const i3 = i * 3;
    const r = Math.pow(Math.random(), 0.7) * radius;
    const branchAngle = ((i % arms) / arms) * Math.PI * 2;
    const spinAngle = r * spin;
    const randX = Math.pow(Math.random(), randomnessPow) * (Math.random() < 0.5 ? 1 : -1) * randomness * r;
    const randY = Math.pow(Math.random(), randomnessPow) * (Math.random() < 0.5 ? 1 : -1) * randomness * 0.2 * r;
    const randZ = Math.pow(Math.random(), randomnessPow) * (Math.random() < 0.5 ? 1 : -1) * randomness * r;
    positions[i3] = Math.cos(branchAngle + spinAngle) * r + randX;
    positions[i3 + 1] = randY;
    positions[i3 + 2] = Math.sin(branchAngle + spinAngle) * r + randZ;
    // Pick palette band based on r and a bit of randomness for color diversity
    const band = Math.min(palette.length - 1, Math.floor((r / radius) * (palette.length - 0.5)));
    const c1 = palette[band];
    const c2 = palette[Math.min(palette.length - 1, band + 1)];
    const mix = (r / radius) * (palette.length - 1) - band;
    const mixed = c1.clone().lerp(c2, Math.min(1, Math.max(0, mix + (Math.random() - 0.5) * 0.4)));
    // Add slight star-color variation
    const intensity = 0.85 + Math.random() * 0.3;
    colors[i3] = mixed.r * intensity;
    colors[i3 + 1] = mixed.g * intensity;
    colors[i3 + 2] = mixed.b * intensity;
    sizes[i] = 0.06 + Math.random() * 0.16;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  g.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  g.setAttribute("size", new THREE.Float32BufferAttribute(sizes, 1));
  return g;
}

function Galaxy() {
  const ref = useRef();
  const geo = useMemo(() => buildGalaxyGeometry({}), []);
  useFrame((_, dt) => { if (ref.current) ref.current.rotation.y += dt * 0.018; });
  return h(
    "points",
    { ref, geometry: geo },
    h("pointsMaterial", {
      size: 0.16,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
  );
}

function GalacticBulge() {
  const geo = useMemo(() => {
    const count = 12000;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const cInside = new THREE.Color("#FFE7B8");
    const cMid = new THREE.Color("#FFB35A");
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      const r = Math.pow(Math.random(), 2.5) * 4.5;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i3 + 1] = r * 0.32 * Math.cos(phi);
      positions[i3 + 2] = r * Math.sin(phi) * Math.sin(theta);
      const m = cInside.clone().lerp(cMid, r / 4.5);
      colors[i3] = m.r; colors[i3 + 1] = m.g; colors[i3 + 2] = m.b;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    g.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    return g;
  }, []);
  return h(
    "points",
    { geometry: geo },
    h("pointsMaterial", {
      size: 0.18, sizeAttenuation: true, vertexColors: true,
      transparent: true, opacity: 0.9, depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
  );
}

function FarStars({ count = 6000 }) {
  const geo = useMemo(() => {
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 90 + Math.random() * 60;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    return g;
  }, [count]);
  return h(
    "points",
    { geometry: geo },
    h("pointsMaterial", {
      size: 0.18, color: "#ffffff", sizeAttenuation: true,
      transparent: true, opacity: 0.55,
    })
  );
}

function NebulaCloud({ position, color, scale = 6, opacity = 0.12 }) {
  return h(
    "mesh",
    { position },
    h("sphereGeometry", { args: [scale, 32, 32] }),
    h("meshBasicMaterial", {
      color, transparent: true, opacity, depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
  );
}

function SunMarker({ onClick }) {
  const ref = useRef();
  useFrame(({ clock }) => {
    if (ref.current) {
      const s = 1 + Math.sin(clock.getElapsedTime() * 2) * 0.08;
      ref.current.scale.set(s, s, s);
    }
  });
  return h(
    "group",
    { position: [0, 0, 0], ref, onClick },
    h(
      "mesh",
      null,
      h("sphereGeometry", { args: [0.42, 24, 24] }),
      h("meshBasicMaterial", { color: "#FFEDA2" })
    ),
    h(
      "mesh",
      null,
      h("sphereGeometry", { args: [0.6, 24, 24] }),
      h("meshBasicMaterial", { color: "#FFE08A", transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending })
    )
  );
}

// Map an exoplanet to a position in the galactic plane (scaled).
// In reality their positions are nearly Sun-adjacent (sub-parsec to kpc).
// We exaggerate distance for visual placement among arms.
function planetPosition(p) {
  // Spread on x-z plane with realistic-ish angular position from RA, slight y wobble.
  const ang = (p.ra_deg / 180) * Math.PI;
  // log-scale distance for compactness: 1 ly → 0.55, 100 ly → 4, 1500 ly → 8
  const d = Math.min(12, 0.5 + Math.log10(1 + p.distance_ly) * 2.4);
  const y = Math.sin((p.dec_deg / 180) * Math.PI) * 0.6;
  return [Math.cos(ang) * d, y, Math.sin(ang) * d];
}

function PlanetMarker({ planet, onClick, selected, hovered, onHover }) {
  const ref = useRef();
  useFrame(({ clock }) => {
    if (ref.current) {
      const s = (selected ? 1.6 : hovered ? 1.3 : 1) +
                Math.sin(clock.getElapsedTime() * 3 + planet.distance_ly) * 0.12;
      ref.current.scale.set(s, s, s);
    }
  });
  const color = planet.habitability > 0.8 ? "#62FFB0" : planet.habitability > 0.6 ? "#9CE0FF" : "#FF9F6F";
  return h(
    "group",
    {
      position: planetPosition(planet),
      onClick: (e) => { e.stopPropagation(); onClick(planet); },
      onPointerOver: (e) => { e.stopPropagation(); onHover(planet.id); },
      onPointerOut: () => onHover(null),
    },
    h(
      "mesh",
      { ref },
      h("sphereGeometry", { args: [0.16, 18, 18] }),
      h("meshBasicMaterial", { color })
    ),
    h(
      "mesh",
      null,
      h("sphereGeometry", { args: [0.36, 18, 18] }),
      h("meshBasicMaterial", { color, transparent: true, opacity: 0.18, blending: THREE.AdditiveBlending })
    )
  );
}

function GalaxyScene({ planets, onSelect, selected, hovered, onHover }) {
  const { camera } = useThree();
  useEffect(() => {
    camera.position.set(0, 14, 22);
    camera.lookAt(0, 0, 0);
  }, [camera]);

  return h(
    Suspense,
    { fallback: null },
    h("color", { attach: "background", args: ["#03040B"] }),
    h("ambientLight", { intensity: 0.3 }),
    h(FarStars, { count: 6000 }),
    h(NebulaCloud, { position: [10, 1, -6], color: "#4D6BFF", scale: 8, opacity: 0.10 }),
    h(NebulaCloud, { position: [-12, -2, 4], color: "#FF6FA0", scale: 9, opacity: 0.09 }),
    h(NebulaCloud, { position: [4, 3, 12], color: "#00D4FF", scale: 6, opacity: 0.11 }),
    h(NebulaCloud, { position: [-6, 4, -10], color: "#A077FF", scale: 7, opacity: 0.08 }),
    h(NebulaCloud, { position: [14, -1, 8], color: "#FFB066", scale: 6.5, opacity: 0.07 }),
    h(NebulaCloud, { position: [-18, 0, -4], color: "#62FFB0", scale: 8, opacity: 0.06 }),
    h(GalacticBulge, null),
    h(Galaxy, null),
    h(SunMarker, null),
    ...planets.map((p) =>
      h(PlanetMarker, {
        key: p.id,
        planet: p,
        onClick: onSelect,
        selected: selected === p.id,
        hovered: hovered === p.id,
        onHover,
      })
    ),
    h(OrbitControls, {
      enablePan: true,
      enableZoom: true,
      enableRotate: true,
      enableDamping: true,
      dampingFactor: 0.08,
      rotateSpeed: 0.6,
      zoomSpeed: 0.9,
      panSpeed: 0.8,
      minDistance: 3,
      maxDistance: 90,
      touches: { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN },
    })
  );
}

export default function Galaxy3D({ planets, onSelect, selectedId, hoveredId, onHover, className = "" }) {
  return (
    <div className={className} style={{ position: "absolute", inset: 0 }}>
      <Canvas camera={{ position: [0, 14, 22], fov: 55, near: 0.1, far: 500 }} dpr={[1, 2]}>
        <GalaxyScene
          planets={planets}
          onSelect={onSelect}
          selected={selectedId}
          hovered={hoveredId}
          onHover={onHover}
        />
      </Canvas>
    </div>
  );
}

export { planetPosition };
